
import { Device } from './types';

type DiscoveryCallback = (devices: Device[]) => void;
type SignalCallback = (data: any) => void;

class DiscoveryService {
  private socket: WebSocket | null = null;
  private myDevice: Device;
  private discoveredDevices: Map<string, Device> = new Map();
  private discoveryCallbacks: DiscoveryCallback[] = [];
  private signalCallbacks: SignalCallback[] = [];
  private reconnectAttempts = 0;
  private keepAliveInterval: any;
  private gridId: string;
  
  private readonly SIGNALING_URL = 'wss://filespread-signaling.onrender.com';

  constructor() {
    this.myDevice = this.generateMyDevice();
    // Logic: Users on the same sub-path or domain are grouped. 
    // This could be expanded to use a hash of the public IP in the future.
    this.gridId = window.location.pathname === '/' ? 'global' : window.location.pathname;
    this.connectSignaling();
  }

  private connectSignaling() {
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.close();
    }

    try {
      this.socket = new WebSocket(this.SIGNALING_URL);
      
      this.socket.onopen = () => {
        this.reconnectAttempts = 0;
        this.send({ type: 'join', device: this.myDevice, gridId: this.gridId });
        this.startKeepAlive();
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'peer_list') {
            const peers = data.peers || [];
            this.discoveredDevices.clear();
            peers.forEach((p: Device) => {
              if (p.id !== this.myDevice.id) {
                this.discoveredDevices.set(p.id, { ...p, lastSeen: Date.now() });
              }
            });
            this.notifyDiscovery();
          }

          if (['pair_request', 'pair_response', 'signal'].includes(data.type)) {
            if (data.to === this.myDevice.id) {
              this.signalCallbacks.forEach(cb => cb(data));
            }
          }
        } catch (err) {}
      };

      this.socket.onclose = () => {
        this.stopKeepAlive();
        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
        this.reconnectAttempts++;
        setTimeout(() => this.connectSignaling(), delay);
      };
    } catch (e) {}
  }

  private send(data: any) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
    }
  }

  private startKeepAlive() {
    this.stopKeepAlive();
    this.keepAliveInterval = setInterval(() => this.send({ type: 'ping' }), 20000);
  }

  private stopKeepAlive() {
    if (this.keepAliveInterval) clearInterval(this.keepAliveInterval);
  }

  private generateMyDevice(): Device {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const device: Device = {
      id: Math.random().toString(36).substring(2, 11),
      name: isMobile ? `Mobile_${Math.floor(Math.random() * 900 + 100)}` : `Desktop_${Math.floor(Math.random() * 900 + 100)}`,
      type: isMobile ? 'mobile' : 'laptop',
      connection: 'wifi',
      lastSeen: Date.now(),
      latency: 0,
      isPinned: false,
      isMe: true
    };
    return device;
  }

  updateMyName(newName: string) {
    this.myDevice.name = newName;
    this.send({ type: 'join', device: this.myDevice, gridId: this.gridId });
  }

  start() {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      this.connectSignaling();
    } else {
      this.send({ type: 'join', device: this.myDevice, gridId: this.gridId });
    }
  }

  refresh() {
    this.send({ type: 'join', device: this.myDevice, gridId: this.gridId });
  }

  stop() {
    this.stopKeepAlive();
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
    }
  }

  subscribe(callback: DiscoveryCallback) {
    this.discoveryCallbacks.push(callback);
    callback(Array.from(this.discoveredDevices.values()));
  }

  private notifyDiscovery() {
    const devices = Array.from(this.discoveredDevices.values());
    this.discoveryCallbacks.forEach(cb => cb(devices));
  }

  getMe() { return this.myDevice; }

  getNetworkId() {
    if (!this.socket) return "Disconnected";
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING: return "Connecting...";
      case WebSocket.OPEN: return "Grid Connected";
      default: return "Offline";
    }
  }

  sendSignal(to: string, type: string, payload: any) {
    this.send({ type: 'signal', from: this.myDevice.id, to, signalType: type, payload });
  }

  sendPairRequest(to: string) {
    this.send({ type: 'pair_request', from: this.myDevice.id, to, name: this.myDevice.name });
  }

  sendPairResponse(to: string, accepted: boolean) {
    this.send({ type: 'pair_response', from: this.myDevice.id, to, accepted });
  }

  onSignal(callback: SignalCallback) {
    this.signalCallbacks.push(callback);
  }
}

export const discovery = new DiscoveryService();
